import { useCallback, useEffect, useState } from 'react'

/**
 * `beforeinstallprompt` is Chromium-only and is not in lib.dom, so the shape the
 * spec defines is declared here rather than widened to `any`.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

function asInstallPrompt(event: Event): BeforeInstallPromptEvent | null {
  return 'prompt' in event && 'userChoice' in event ? (event as BeforeInstallPromptEvent) : null
}

const STANDALONE_QUERY = '(display-mode: standalone)'

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia(STANDALONE_QUERY).matches) return true
  // iOS predates `display-mode` and exposes its own flag on navigator instead.
  const legacy = window.navigator as Navigator & { standalone?: boolean }
  return legacy.standalone === true
}

/**
 * iOS has no install event at all — the only route is Share → Add to Home
 * Screen, and only from Safari. Chrome and Firefox on iOS are WebKit under the
 * hood but cannot install, so telling their users to use Share would be wrong.
 */
function detectIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const iOS =
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports a desktop user agent; the touch points give it away.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  return iOS && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)
}

const IOS_SAFARI = detectIosSafari()

export type InstallState =
  /** Already running from the home screen / app window. */
  | 'installed'
  /** Chromium handed us an install event we can fire on demand. */
  | 'promptable'
  /** iOS Safari: manual Share → Add to Home Screen. */
  | 'ios-manual'
  /** No install path we can drive or describe precisely. */
  | 'unsupported'

export interface InstallPrompt {
  state: InstallState
  /** Resolves once the user has answered the browser's own dialog. */
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>
}

export function useInstallPrompt(): InstallPrompt {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [installed, setInstalled] = useState(isStandalone)

  useEffect(() => {
    function handleBeforeInstallPrompt(event: Event) {
      // Without preventDefault Chrome shows its own mini-infobar; the button in
      // this section should be the single entry point.
      event.preventDefault()
      const prompt = asInstallPrompt(event)
      if (prompt) setDeferred(prompt)
    }

    function handleInstalled() {
      setInstalled(true)
      setDeferred(null)
    }

    const media = window.matchMedia(STANDALONE_QUERY)
    function handleDisplayModeChange(event: MediaQueryListEvent) {
      setInstalled(event.matches)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleInstalled)
    media.addEventListener('change', handleDisplayModeChange)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleInstalled)
      media.removeEventListener('change', handleDisplayModeChange)
    }
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferred) return 'unavailable' as const
    await deferred.prompt()
    const { outcome } = await deferred.userChoice
    // The event is single-use. Chromium fires a fresh one later if declined.
    setDeferred(null)
    return outcome
  }, [deferred])

  const state: InstallState = installed
    ? 'installed'
    : deferred
      ? 'promptable'
      : IOS_SAFARI
        ? 'ios-manual'
        : 'unsupported'

  return { state, promptInstall }
}
